import React from "react";
import { Box, Typography, Button, IconButton } from "@mui/material";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { PrimaryTooltip } from "../../../../../Components/StyledComponent";
import PillCheckbox from "../../../../../Components/PillCheckbox";
import {
    getPackRecencyBadge,
    formatReplacedDate,
    getReplaceBlockedReason,
    isEditPackVisible,
    isReplaceVisible,
} from "../../../../../Helper/DigitalTwin/BatteryComponentsHelper";
import { getLocalDateFromUTC } from "../../../../../Helper/DatePicker/DateConverter";

// A single replaceable pack card (Figma: Components > Battery).
//
// Two modes:
//   default   - per-card Replace button + history icon. Replace is gated on the
//               bus being Non-operational (UI-only); the history icon is gated
//               on the pack having replacement history.
//   selection - "Replace Multiple" is active: a checkbox replaces the two
//               action controls and the whole card toggles. Packs that cannot
//               be replaced are dimmed with the checkbox disabled.
export default function PackCard({
    pack,
    isNonOperational = false,
    canEdit = true,
    canEditDetails = false,
    onReplace = () => {},
    onEditDetails = () => {},
    onHistory = () => {},
    selectionMode = false,
    selected = false,
    selectable = true,
    onToggleSelect = () => {},
}) {
    // lastReplacedAt is a backend UTC datetime; show it as the user's local
    // calendar day (manufacturingDate stays as-is — it is a tz-less date).
    const lastReplacedLocal = pack?.lastReplacedAt
        ? getLocalDateFromUTC(pack.lastReplacedAt, "YYYY-MM-DD")
        : null;
    const badge = getPackRecencyBadge(lastReplacedLocal);
    // Without permission the button is not rendered at all, so there is no
    // tooltip to show: the bus being operational is the only reason left that the
    // user can act on.
    const showReplace = isReplaceVisible({ canEdit });
    const replaceTooltip = getReplaceBlockedReason({ isNonOperational });
    const replaceDisabled = replaceTooltip !== null;
    const historyDisabled = !pack?.hasReplacementHistory;
    // Not disabled by isNonOperational: see isEditPackVisible. A correction is a data
    // fix, so the operational gate that applies to Replace does not apply here.
    const showEditDetails = isEditPackVisible({ canEditDetails, pack });

    const replaceButton = (
        <span>
            <Button
                data-testid={`pack-replace-${pack?.packConfigID}`}
                disableElevation
                disableRipple
                disabled={replaceDisabled}
                onClick={() => onReplace(pack)}
                sx={{
                    textTransform: "none",
                    fontSize: "14px",
                    fontWeight: 500,
                    lineHeight: "20px",
                    color: "#344054",
                    border: "1px solid #D0D5DD",
                    borderRadius: "999px",
                    padding: "8px 18px",
                    minWidth: 0,
                    backgroundColor: "#fff",
                    transition: "background-color .15s, border-color .15s, color .15s",
                    "&:hover": { backgroundColor: "#F7F7FF", borderColor: "#6D72F6", color: "#6D72F6" },
                    "&.Mui-disabled": { color: "#BDBDBD", borderColor: "#E6E6E6", backgroundColor: "#fff" },
                }}
            >
                Replace
            </Button>
        </span>
    );

    // In selection mode the whole card is the hit target. The checkbox inside it
    // stops propagation so a click there toggles once, not twice (card + box).
    const cardToggle = selectionMode && selectable ? () => onToggleSelect(pack) : undefined;
    const checkboxToggle = (event) => {
        event.stopPropagation();
        onToggleSelect(pack);
    };

    return (
        <Box
            data-testid={`pack-card-${pack?.absPackNo}`}
            onClick={cardToggle}
            sx={{
                position: "relative",
                border: `1px solid ${selected ? "#6D72F6" : "#E6E6E6"}`,
                // Outer ring rather than a 2px border: reads as the thicker
                // selected outline without reflowing the grid by a pixel.
                boxShadow: selected ? "0 0 0 1px #6D72F6" : "none",
                borderRadius: "8px",
                backgroundColor: "#fff",
                padding: "14px 16px",
                display: "flex",
                flexDirection: "column",
                gap: "10px",
                cursor: cardToggle ? "pointer" : "default",
                // Dim a pack that cannot be picked (no stable slot number, or the
                // bus/permission gate is closed) so the disabled checkbox reads
                // as intentional.
                opacity: selectionMode && !selectable ? 0.55 : 1,
                transition: "border-color .15s, box-shadow .15s",
            }}
        >
            {badge && (
                <Box
                    sx={{
                        position: "absolute",
                        // Top-right corner, fully ABOVE the card border (bottom edge
                        // resting on the top border), per Figma — not straddling it.
                        top: "-21px",
                        right: "16px",
                        backgroundColor: "#E4EDC4",
                        color: "#52631E",
                        fontSize: "11px",
                        fontWeight: 500,
                        lineHeight: "16px",
                        // Tab sitting on the top border: rounded top, flat bottom.
                        borderRadius: "4px 4px 0 0",
                        padding: "3px 10px",
                        whiteSpace: "nowrap",
                    }}
                >
                    {badge}
                </Box>
            )}

            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: "8px",
                    // Keeps the row the same height in both modes (the 38px action
                    // buttons are taller than the checkbox), so entering selection
                    // mode doesn't make every card jump.
                    minHeight: "38px",
                }}
            >
                {selectionMode ? (
                    <PillCheckbox
                        plain
                        testId={`pack-select-${pack?.absPackNo}`}
                        checked={selected}
                        disabled={!selectable}
                        onChange={checkboxToggle}
                        label={pack?.label}
                        labelSize={14}
                        labelWeight={600}
                    />
                ) : (
                    <>
                        <Typography
                            sx={{ fontSize: "14px", fontWeight: 600, color: "#1A1A1A" }}
                        >
                            {pack?.label}
                        </Typography>

                        <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
                            {/* The history icon stays either way: viewing a pack's
                                past is a read, not gated on the write permission. */}
                            {showReplace &&
                                (replaceDisabled ? (
                                    <PrimaryTooltip title={replaceTooltip} placement="top">
                                        {replaceButton}
                                    </PrimaryTooltip>
                                ) : (
                                    replaceButton
                                ))}

                            {showEditDetails && (
                                <PrimaryTooltip title="Edit pack details" placement="top">
                                    <IconButton
                                        data-testid={`pack-edit-${pack?.absPackNo}`}
                                        onClick={() => onEditDetails(pack)}
                                        sx={{
                                            width: 38,
                                            height: 38,
                                            border: "1px solid #D0D5DD",
                                            borderRadius: "8px",
                                            color: "#667085",
                                            backgroundColor: "#fff",
                                            transition: "background-color .15s, border-color .15s, color .15s",
                                            "&:hover": { backgroundColor: "#F7F7FF", borderColor: "#6D72F6", color: "#6D72F6" },
                                        }}
                                    >
                                        <EditOutlinedIcon sx={{ fontSize: 19 }} />
                                    </IconButton>
                                </PrimaryTooltip>
                            )}

                            <IconButton
                                data-testid={`pack-history-${pack?.packConfigID}`}
                                disabled={historyDisabled}
                                onClick={() => onHistory(pack)}
                                sx={{
                                    width: 38,
                                    height: 38,
                                    border: "1px solid #D0D5DD",
                                    borderRadius: "8px",
                                    color: "#667085",
                                    backgroundColor: "#fff",
                                    transition: "background-color .15s, border-color .15s, color .15s",
                                    "&:hover": { backgroundColor: "#F7F7FF", borderColor: "#6D72F6", color: "#6D72F6" },
                                    "&.Mui-disabled": { color: "#C8C8C8", borderColor: "#E6E6E6" },
                                }}
                            >
                                <HistoryOutlinedIcon sx={{ fontSize: 19 }} />
                            </IconButton>
                        </Box>
                    </>
                )}
            </Box>

            <Box
                sx={{
                    backgroundColor: "#F5F5F5",
                    borderRadius: "6px",
                    padding: "8px 12px",
                    fontSize: "12px",
                    color: "#6B6B6B",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                }}
                title={pack?.serial || ""}
            >
                {pack?.serial || "-"}
            </Box>

            {/* Manufacturing date (left) and Last replaced date (right). Labels
                are shortened; hovering shows the full label. */}
            <Box
                sx={{
                    display: "flex",
                    alignItems: "flex-end",
                    justifyContent: "space-between",
                    gap: "12px",
                }}
            >
                <Box sx={{ minWidth: 0 }}>
                    <PrimaryTooltip title="Manufacturing date" placement="top">
                        <Typography
                            component="span"
                            sx={{ fontSize: "12px", color: "#8D8D8D", cursor: "default" }}
                        >
                            Mfg date
                        </Typography>
                    </PrimaryTooltip>
                    <Typography sx={{ fontSize: "12px", fontWeight: 500, color: "#494949" }}>
                        {formatReplacedDate(pack?.manufacturingDate)}
                    </Typography>
                </Box>
                <Box sx={{ minWidth: 0, textAlign: "right" }}>
                    <PrimaryTooltip title="Last replaced date" placement="top">
                        <Typography
                            component="span"
                            sx={{ fontSize: "12px", color: "#8D8D8D", cursor: "default" }}
                        >
                            Last repl. date
                        </Typography>
                    </PrimaryTooltip>
                    <Typography sx={{ fontSize: "12px", fontWeight: 500, color: "#494949" }}>
                        {formatReplacedDate(lastReplacedLocal)}
                    </Typography>
                </Box>
            </Box>
        </Box>
    );
}
